import { ArrowLeftIcon, ArrowRightIcon } from '@eleveo/icons-react';
import { Button } from '@zoom/components';
import { isBefore, isEqual, startOfDay } from 'date-fns';
import { TFunction } from 'i18next';
import React from 'react';
import { Field } from 'react-final-form';

import { DatePickerField } from '../../../components/DatePicker';
import { DatePickerValueType } from '../../../components/DatePicker/DatePicker';
import { createEndOfDateRange } from '../../../utils/dateRange';
import { PREVIOUS_DAY_BUTTON_TAG, NEXT_DAY_BUTTON_TAG, SCHEDULING_DATE_PICKER_INPUT_TAG } from '../constants';

export interface DaySwitcherBarProps {
  dataTestId?: string;
  datePickerFormatKey?: string;
  handlePreviousDay: () => void;
  handleNextDay: () => void;
  handleDateChange: (value: DatePickerValueType) => void;
  nextDayButtonDataTestId?: string;
  previousDayButtonDataTestId?: string;
  startTime: Date;
  endTime: Date;
  pickedTime: Date;
  t: TFunction;
}

const isFirstDay = (current: Date, startDate: Date) => isEqual(current, startDate);
const isLastDay = (current: Date, endDate: Date) => isBefore(endDate, createEndOfDateRange(current));

export const DaySwitcherBar: React.FC<DaySwitcherBarProps> = ({
  dataTestId = SCHEDULING_DATE_PICKER_INPUT_TAG,
  datePickerFormatKey = 'date.formats.defaultIncludingWeekday',
  handlePreviousDay,
  handleNextDay,
  handleDateChange,
  nextDayButtonDataTestId = NEXT_DAY_BUTTON_TAG,
  previousDayButtonDataTestId = PREVIOUS_DAY_BUTTON_TAG,
  startTime,
  endTime,
  pickedTime,
  t,
}) => {
  const outOfRangeDays = [startOfDay(endTime)];

  const SelectedDateField = DatePickerField({
    dateFormat: t(datePickerFormatKey),
    label: t('date.selectedDate'),
    dataTestId,
    noValidation: true,
    minDate: startTime,
    maxDate: endTime,
    customOnChange: handleDateChange,
    daysWithTooltip: outOfRangeDays,
    tooltipMessage: t('schedule.extraDayForOverflowingShifts'),
  });

  return (
    <div className="scheduling-header-form__day-switch-bar">
      <Button
        data-test-id={previousDayButtonDataTestId}
        onClick={handlePreviousDay}
        disabled={isFirstDay(pickedTime, startTime)}
        title={t('date.previousDay')}
        kind="icon-bordered"
      >
        <ArrowLeftIcon className="text-chart-300" />
      </Button>
      <div className="scheduling-header-form__day-switch-bar__selected-date">
        <Field name="date" component={SelectedDateField} />
      </div>
      <Button
        data-test-id={nextDayButtonDataTestId}
        onClick={handleNextDay}
        disabled={isLastDay(pickedTime, endTime)}
        title={t('date.nextDay')}
        kind="icon-bordered"
      >
        <ArrowRightIcon className="text-chart-300" />
      </Button>
    </div>
  );
};
